// ----- Phase 5.X-settings-schema-ui — V1 SETTINGS_SCHEMA port -----
//
// V1's `~/autoectd/src/utils/settings.js` SETTINGS_SCHEMA is the source of
// truth for the 13 semantic settings keys that V1's SettingsScreen.jsx
// renders as toggles + a single slider, grouped into 7 sections. This
// module ports that schema verbatim. The single-source-of-truth
// constraint (prompt guardrail #2): components don't hard-code keys;
// they read from SETTINGS_SCHEMA.
//
// SCOPE NOTE: this phase ports the UI only. The BEHAVIORAL wiring for
// each key (e.g., `autoClassifyOnUpload` actually triggering
// classification on document upload) is out of scope and will be done
// as per-setting follow-up phases (estimated ~5-8 days total to wire
// all 13).

export type SettingsSection =
  | 'Classification'
  | 'Admin Forms'
  | 'Validation'
  | 'Hyperlinking'
  | 'Compliance'
  | 'Learning'
  | 'Monitoring'
  // Phase 5.X-company-assets-letterhead — new section for org branding
  // controls (letterhead overlay toggle today; future: watermarks, brand
  // colours).
  | 'Branding'
  // Phase 7.2 — Document Generation section for cover letter mode.
  | 'Document Generation'
  // phase-a4-assistant-routing-picker — org controls for AI assistant
  // model routing (default tier + max tier cap).
  | 'Assistant'
  // Phase attention-surface-setting — user opt-in for the dashboard feed.
  | 'Dashboard';

export type SettingsControlType = 'toggle' | 'slider' | 'select';

export type SettingsLevel = 'user' | 'org';

/** A selectable option for a 'select' control type. */
export interface SettingsSelectOption {
  readonly value: string;
  readonly label: string;
  readonly description?: string;
}

export interface SettingsSchemaEntry {
  /** Stable identifier; matches the key written to the Settings table. */
  readonly key: string;
  /** Fallback value when no row exists in any tier (post 3-tier merge). */
  readonly defaultValue: boolean | number | string;
  /** Determines which scope endpoint writes go to (user vs org). */
  readonly level: SettingsLevel;
  readonly controlType: SettingsControlType;
  readonly section: SettingsSection;
  /** Short label shown next to the control (≤40 chars in V1). */
  readonly label: string;
  /** Subtitle text under the label (V1's per-key help copy). */
  readonly description: string;
  /** Slider min — required when controlType='slider'. */
  readonly min?: number;
  /** Slider max — required when controlType='slider'. */
  readonly max?: number;
  /** Slider step — required when controlType='slider'. */
  readonly step?: number;
  /** Unit suffix rendered alongside the slider value (e.g., '%'). */
  readonly unit?: string;
  /** Required when controlType='select'. */
  readonly options?: ReadonlyArray<SettingsSelectOption>;
}

/**
 * The 13 V1 schema entries, in declaration order matching V1's grouping
 * (Classification → Admin Forms → … → Monitoring). Section order is
 * preserved via SECTION_ORDER below — relying on iteration order
 * would be fragile because `groupSchemaBySection()` returns a Record.
 */
export const SETTINGS_SCHEMA: ReadonlyArray<SettingsSchemaEntry> = [
  // ----- Classification (3 keys) -----
  {
    key: 'autoClassifyOnUpload',
    defaultValue: true,
    level: 'user',
    controlType: 'toggle',
    section: 'Classification',
    label: 'Auto-classify after upload',
    description:
      'Automatically send uploaded documents to the AI classifier. Turn off to classify manually.',
  },
  {
    key: 'autoConfirmHighConfidence',
    defaultValue: false,
    level: 'user',
    controlType: 'toggle',
    section: 'Classification',
    // V1 label verbatim: "Auto-confirm documents above threshold"
    label: 'Auto-confirm documents above threshold',
    description:
      'Automatically confirm documents classified above the confidence threshold below. Confirmed documents proceed to compilation.',
  },
  {
    key: 'confidenceThreshold',
    defaultValue: 90,
    level: 'user',
    controlType: 'slider',
    section: 'Classification',
    label: 'Confidence threshold (%)',
    description:
      'Documents classified above this percentage are auto-confirmed when the setting above is on. Lower values confirm more documents but with higher misclassification risk. Recommended: 90%.',
    min: 75,
    max: 99,
    step: 1,
    unit: '%',
  },

  // Phase 8.2 — review tier threshold + batch classify toggle
  {
    key: 'reviewConfidenceThreshold',
    defaultValue: 70,
    level: 'user',
    controlType: 'slider',
    section: 'Classification',
    label: 'Review tier threshold (%)',
    description:
      'Documents classified below the auto-confirm threshold but above this value are routed to "Review Required". Documents below this value require manual attention. Recommended: 70%.',
    min: 50,
    max: 89,
    step: 1,
    unit: '%',
  },
  {
    key: 'batchClassifyEnabled',
    defaultValue: false,
    level: 'org',
    controlType: 'toggle',
    section: 'Classification',
    label: 'Enable batch classify-all',
    description:
      'Allow classifying all unclassified documents in a submission in one operation. Org-level setting.',
  },
  // Phase 8.3 — Prompt Library Layer 2 toggle.
  {
    key: 'promptLibraryClassificationEnabled',
    defaultValue: false,
    level: 'user',
    controlType: 'toggle',
    section: 'Classification',
    label: 'Use prompt library for classification',
    description:
      'When on, the classifier first checks your Prompt Library for a custom Classification prompt matching the document. If a suitable prompt is found it is used instead of the default. Falls back to the default classifier when no match is found.',
  },

  // ----- Admin Forms (2 keys) -----
  {
    key: 'autoPopulateOnConfirm',
    defaultValue: false,
    level: 'user',
    controlType: 'toggle',
    section: 'Admin Forms',
    // V1 label verbatim: "Auto-populate forms automatically"
    label: 'Auto-populate forms automatically',
    description:
      'Automatically fill in admin forms using your company profile and submission data when a document is confirmed.',
  },
  {
    key: 'autoSyncCoverLetter',
    defaultValue: false,
    level: 'user',
    controlType: 'toggle',
    section: 'Admin Forms',
    label: 'Warn when cover letter is out of sync',
    description:
      'Show a warning when your document list changes after the cover letter was generated.',
  },

  // ----- Validation (1 key) -----
  {
    key: 'continuousValidation',
    defaultValue: false,
    level: 'user',
    controlType: 'toggle',
    section: 'Validation',
    // V1 label verbatim: "Continuous background validation score"
    label: 'Continuous background validation score',
    description:
      'Validate documents automatically as you work. Turn off for large submissions if validation feels slow.',
  },

  // ----- Hyperlinking (2 keys) -----
  {
    key: 'autoInjectLinks',
    defaultValue: false,
    level: 'user',
    controlType: 'toggle',
    section: 'Hyperlinking',
    // navigation-foundation (U01) — truthful copy. The publish-time pass STYLES
    // the cross-document links that already exist in your PDFs; it does NOT
    // generate or insert new hyperlinks (that text-pattern detection is a
    // separate, not-yet-shipped phase). The V1 "auto-inject" wording overstated
    // the behaviour, so it was corrected rather than kept verbatim.
    label: 'Style cross-document links at publish time',
    description:
      'When publishing a Final Submission Package, run a pass that restyles the cross-document links already present in your PDFs. This does not create new hyperlinks.',
  },
  {
    key: 'blueUnderlineLinks',
    defaultValue: true,
    level: 'user',
    controlType: 'toggle',
    section: 'Hyperlinking',
    label: 'Blue underline on cross-document links',
    description:
      'Restyle existing cross-document links with blue underline formatting per agency display guidelines. Existing links are restyled; no new links are created.',
  },

  // ----- Compliance (3 keys — all org-level) -----
  {
    key: 'blockPublishIfUnconfirmed',
    defaultValue: true,
    level: 'org',
    controlType: 'toggle',
    section: 'Compliance',
    label: 'Block publish if unconfirmed documents exist',
    description: 'Prevent publishing until all documents in the submission are confirmed. Org-level setting.',
  },
  {
    key: 'prePublishChecklist',
    defaultValue: false,
    level: 'org',
    controlType: 'toggle',
    section: 'Compliance',
    label: 'Show pre-publish checklist before publishing',
    description: 'Show a checklist of required items to verify before publishing. Org-level setting.',
  },
  {
    key: 'requireReauthAtPublish',
    defaultValue: false,
    level: 'org',
    controlType: 'toggle',
    section: 'Compliance',
    label: 'Require re-authentication at publish',
    description: 'Require re-authentication with Microsoft credentials before publishing as an electronic signature. Org-level setting.',
  },

  // ----- Learning (1 key) -----
  {
    key: 'learningFromCorrections',
    defaultValue: false,
    level: 'user',
    controlType: 'toggle',
    section: 'Learning',
    label: 'Use past corrections to improve classification',
    description: 'Feed your past classification corrections back into the AI as examples to improve accuracy over time.',
  },

  // ----- Monitoring (1 key) -----
  {
    key: 'monitorEsgAcknowledgement',
    defaultValue: false,
    level: 'user',
    controlType: 'toggle',
    section: 'Monitoring',
    label: 'Monitor for ESG acknowledgement after publish',
    description: 'Automatically check for agency acknowledgement after ESG submission. Turn off to manage ACK confirmation manually.',
  },

  // ----- Branding (1 key) — Phase 5.X-company-assets-letterhead -----
  {
    key: 'letterheadMode',
    defaultValue: false,
    level: 'org',
    controlType: 'toggle',
    section: 'Branding',
    label: 'Apply letterhead to generated documents',
    description:
      'Overlays uploaded letterhead image on cover letters and admin forms',
  },

  // ----- Dashboard (1 key) — Phase attention-surface-setting -----
  {
    key: 'showAttentionSurface',
    defaultValue: false,
    level: 'user',
    controlType: 'toggle',
    section: 'Dashboard',
    label: 'Dashboard attention feed',
    description:
      "Show a 'Needs your attention' panel above the submissions list highlighting items requiring action across all your submissions. Useful when managing multiple submissions.",
  },

  // ----- Document Generation (1 key) — Phase 7.2 -----
  {
    key: 'coverLetterMode',
    defaultValue: 'text-only',
    level: 'org',
    controlType: 'select',
    section: 'Document Generation',
    label: 'Cover letter letterhead mode',
    description:
      'Controls how the company letterhead is applied when generating cover letter PDFs.',
    options: [
      {
        value: 'text-only',
        label: 'Text only',
        description: 'Plain PDF — no letterhead applied.',
      },
      {
        value: 'logo-overlay',
        label: 'Logo overlay',
        description: 'Overlays the company logo on the first page of the generated PDF.',
      },
      {
        value: 'word-template',
        label: 'Word template',
        description:
          'Uses the company letterhead .docx template asset as the base document.',
      },
    ],
  },
  // ---- phase-a5 — assistant on/off per tenant ----------------------------
  {
    key: 'assistantEnabled',
    defaultValue: true,
    level: 'org',
    controlType: 'toggle',
    section: 'Assistant',
    label: 'Enable AI assistant',
    description:
      'Turns the AI assistant on or off for everyone in the organisation. When off, the panel is hidden and the API refuses assistant requests.',
  },
  // ---- phase-a4-assistant-routing-picker — assistant model routing -------
  {
    key: 'assistantDefaultTier',
    defaultValue: 'auto',
    level: 'org',
    controlType: 'select',
    section: 'Assistant',
    label: 'Default model tier',
    description:
      'The model tier the assistant uses when the user has not picked one. Auto routes each question to the recommended tier. The model catalog determines which model serves each tier.',
    options: [
      { value: 'auto', label: 'Auto (recommended)', description: 'Route each question to the recommended tier.' },
      { value: 'fast', label: 'Fast', description: 'Fastest and cheapest; best for lookups.' },
      { value: 'balanced', label: 'Balanced', description: 'Balanced speed and reasoning.' },
      { value: 'advanced', label: 'Advanced', description: 'Deepest reasoning; highest cost.' },
    ],
  },
  {
    key: 'assistantMaxTier',
    defaultValue: 'balanced',
    level: 'org',
    controlType: 'select',
    section: 'Assistant',
    label: 'Maximum model tier',
    description:
      'Hard cap enforced on the server: no assistant answer runs above this tier, including user selections.',
    options: [
      { value: 'fast', label: 'Fast', description: 'Cap at the fast tier.' },
      { value: 'balanced', label: 'Balanced', description: 'Cap at the balanced tier (default).' },
      { value: 'advanced', label: 'Advanced', description: 'No cap below the deepest tier.' },
    ],
  },
  // ---- phase-classification-model-selection — classification's model ------
  // Deliberately in the Assistant section: it selects from the SAME
  // admin-configured catalog and obeys the SAME "Maximum model tier" cap
  // immediately above, so the two settings must be read together.
  //
  // The options mirror the shipped default catalog. When an org overrides the
  // catalog (`assistantModelCatalog`), the authority is the server: the
  // classify selector reads /api/v1/classification/model-catalog, and an id
  // that is unknown, disabled, or above the cap falls back through the
  // precedence chain instead of failing a classification.
  {
    key: 'classificationDefaultModel',
    defaultValue: 'auto',
    level: 'org',
    controlType: 'select',
    section: 'Assistant',
    label: 'Default model for classification',
    description:
      'The model used to classify documents when nobody picks one. Classification is a bounded task and every result is human-confirmed, so a cheaper model is often enough. Never runs above the maximum model tier above.',
    options: [
      {
        value: 'auto',
        label: 'Automatic (capped tier default)',
        description: "Use the catalog's default model for the tier your organisation is capped at.",
      },
      {
        value: 'claude-haiku-4-5',
        label: 'Claude Haiku 4.5',
        description: 'Fast tier — cheapest; often sufficient for section assignment.',
      },
      {
        value: 'claude-sonnet-5',
        label: 'Claude Sonnet 5',
        description: 'Balanced tier — stronger reasoning on ambiguous documents.',
      },
      {
        value: 'claude-fable-5',
        label: 'Claude Fable 5',
        description: 'Advanced tier — highest cost; only runs if the cap allows it.',
      },
      {
        value: 'claude-opus-4-8',
        label: 'Claude Opus 4.8',
        description: 'Advanced tier — only runs if the cap allows it.',
      },
    ],
  },
  // ---- phase-a5 — conversation retention window --------------------------
  {
    key: 'assistantRetentionDays',
    defaultValue: '90',
    level: 'org',
    controlType: 'select',
    section: 'Assistant',
    label: 'Conversation retention',
    description:
      'How long assistant conversations are kept before the nightly purge removes them. Audit-log entries about assistant use are unaffected.',
    options: [
      { value: '30', label: '30 days' },
      { value: '90', label: '90 days (default)' },
      { value: '365', label: '1 year' },
      { value: 'unlimited', label: 'Unlimited', description: 'Never purge conversations.' },
    ],
  },
];

/**
 * Section render order — components iterate this array (not the keys
 * of a Record) so the visual order is stable and explicit.
 */
export const SECTION_ORDER: ReadonlyArray<SettingsSection> = [
  'Classification',
  'Admin Forms',
  'Validation',
  'Hyperlinking',
  'Compliance',
  'Learning',
  'Monitoring',
  'Branding',
  'Document Generation',
  'Dashboard',
  'Assistant',
];

/**
 * Group the flat schema by section. Returns a Record keyed by section
 * name; each value preserves the original SETTINGS_SCHEMA declaration
 * order within that section.
 */
export function groupSchemaBySection(): Record<SettingsSection, SettingsSchemaEntry[]> {
  const out: Record<SettingsSection, SettingsSchemaEntry[]> = {
    Classification: [],
    'Admin Forms': [],
    Validation: [],
    Hyperlinking: [],
    Compliance: [],
    Learning: [],
    Monitoring: [],
    Branding: [],
    'Document Generation': [],
    Dashboard: [],
      Assistant: [],
  };
  for (const entry of SETTINGS_SCHEMA) {
    out[entry.section].push(entry);
  }
  return out;
}

/**
 * Look up a schema entry by key. Returns null when the key is not in
 * the schema — callers can fall back to the generic key-value editor.
 */
export function findSchemaEntry(key: string): SettingsSchemaEntry | null {
  return SETTINGS_SCHEMA.find((e) => e.key === key) ?? null;
}

/**
 * T-17 (D6) — the workflow keys a SUBMISSION may override. The backend's
 * settings registry (src/services/settings/registry.ts) is the authority — it
 * refuses any other key at the submission scope with 400
 * SettingScopeNotAllowed — and tests/services/settings/registry.test.ts pins
 * this list against it. The submission settings UI offers only these keys.
 */
export const SUBMISSION_CAPABLE_KEYS: readonly string[] = [
  'autoClassifyOnUpload',
  'autoConfirmHighConfidence',
  'confidenceThreshold',
  'reviewConfidenceThreshold',
  'promptLibraryClassificationEnabled',
  'learningFromCorrections',
  'autoPopulateOnConfirm',
  'autoSyncCoverLetter',
  'continuousValidation',
  'autoInjectLinks',
  'blueUnderlineLinks',
  'monitorEsgAcknowledgement',
  'coverLetterMode',
];

/**
 * Stable set of all schema keys, used to filter "schema-known" vs
 * "ad-hoc" keys at the SettingsPage level.
 */
export const SCHEMA_KEYS: ReadonlySet<string> = new Set(
  SETTINGS_SCHEMA.map((e) => e.key),
);
